import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { BILLING_BRAND, getSubscriptionPaymentProviderMeta } from '../data/billingBrandConfig';

// =====================================================================
// useSubscriptionStatus — NÚCLEO (viaja por sync). Reemplaza TrialContext.
// ---------------------------------------------------------------------
// Brand-agnostic: toda referencia de marca/pasarela sale de
// billingBrandConfig (capa protegida por app).
//
// INACTIVIDAD POR FLAG (clave del plan):
//   El módulo de cobro se habilita por organización vía
//   organizations.custom_modules incluyendo BILLING_BRAND.billingFlag
//   ('suscripcion-billing'). Por defecto NINGUNA org de Impulza lo tiene
//   → el provider devuelve { status:'active', isReadOnly:false } y NO
//   hace ningún fetch de suscripción/uso. Impacto cero para clientes.
// =====================================================================

const USAGE_KINDS = ['sales', 'invoices', 'purchases', 'collections', 'payments'];
const ZERO_USAGE = USAGE_KINDS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
const ZERO_LIMITS = USAGE_KINDS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
export const USAGE_CHANGED_EVENT = 'billing:usage-changed';

const SubscriptionContext = createContext({});

// Estado inerte (flag apagado): la app se comporta como si no existiera el cobro.
const INERT_VALUE = {
  daysLeft: 0,
  isTrialing: false,
  isActive: true,
  isPending: false,
  isGrace: false,
  isExpired: false,
  isBlocked: false,
  isReadOnly: false,
  showWarning: false,
  showGraceWarning: false,
  graceDaysLeft: 0,
  paymentUrl: null,
  currentPlanKey: null,
  subscriptionExpiresAt: null,
  status: 'active',
  billingEnabled: false,
  billingProvider: BILLING_BRAND.paymentProvider,
  billingProviderLabel: getSubscriptionPaymentProviderMeta(BILLING_BRAND.paymentProvider).label,
  billingProviderMeta: getSubscriptionPaymentProviderMeta(BILLING_BRAND.paymentProvider),
  limits: { ...ZERO_LIMITS },
  maxUsers: 0,
  userCount: 0,
  usage: { ...ZERO_USAGE, cycle_start: null, cycle_end: null },
  usageMap: USAGE_KINDS.reduce((acc, k) => ({ ...acc, [k]: { used: 0, limit: 0, unlimited: true, reached: false, percentage: 0, remaining: null } }), {}),
  usersLimit: { used: 0, limit: 0, unlimited: true, reached: false, percentage: 0, remaining: null },
  canDo: () => true,
  organization: null,
  currentPlan: null,
  latestSubscription: null,
  loadingTrial: false,
  refreshTrial: () => {},
};

function orgHasBillingFlag(org) {
  return Array.isArray(org?.custom_modules) && org.custom_modules.includes(BILLING_BRAND.billingFlag);
}

export const SubscriptionProvider = ({ children }) => {
  const { profile, organization: authOrganization } = useAuth();
  const [org, setOrg] = useState(null);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [latestSubscription, setLatestSubscription] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(true);
  const [usage, setUsage] = useState({ ...ZERO_USAGE, cycle_start: null, cycle_end: null });
  const [userCount, setUserCount] = useState(0);

  const orgId = profile?.organization_id;

  const fetchOrg = useCallback(async ({ force = false } = {}) => {
    if (!orgId) {
      setLoadingTrial(false);
      return;
    }
    try {
      // Reusar la row que ya trajo AuthContext evita repetir el fetch de
      // organizations en el arranque (ref IMPULZA-4). El refresh por realtime
      // o evento de uso pasa force:true para traer datos frescos.
      let orgData = (!force && authOrganization?.id === orgId) ? authOrganization : null;
      if (!orgData) {
        const { data, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', orgId)
          .single();
        if (orgError) throw orgError;
        orgData = data;
      }
      setOrg(orgData);

      const enabled = orgHasBillingFlag(orgData);
      setBillingEnabled(enabled);

      // FLAG OFF → no se consulta nada más. Inerte.
      if (!enabled) {
        setCurrentPlan(null);
        setLatestSubscription(null);
        setUsage({ ...ZERO_USAGE, cycle_start: null, cycle_end: null });
        setUserCount(0);
        return;
      }

      // FLAG ON → lógica completa de suscripción
      const planKey = orgData?.current_plan_key || orgData?.plan || 'basic';
      try {
        const { data: planData } = await supabase
          .from('subscription_plans').select('*').eq('key', planKey).eq('is_active', true).single();
        setCurrentPlan(planData ? {
          ...planData,
          monthly_limits: planData.monthly_limits || { ...ZERO_LIMITS },
          max_users: Number(planData.max_users) || 1,
          currency: planData.currency || BILLING_BRAND.currency,
        } : null);
      } catch (_) { setCurrentPlan(null); }

      try {
        const [usageResponse, userCountResponse] = await Promise.allSettled([
          supabase.rpc('get_org_usage', { p_org_id: orgId }),
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('organization_id', orgId),
        ]);
        if (usageResponse.status === 'fulfilled') {
          const row = Array.isArray(usageResponse.value?.data) ? usageResponse.value.data[0] : usageResponse.value?.data;
          if (row) {
            setUsage({
              cycle_start: row.cycle_start ? new Date(row.cycle_start) : null,
              cycle_end: row.cycle_end ? new Date(row.cycle_end) : null,
              sales: Number(row.sales) || 0,
              invoices: Number(row.invoices) || 0,
              purchases: Number(row.purchases) || 0,
              collections: Number(row.collections) || 0,
              payments: Number(row.payments) || 0,
            });
          }
        }
        if (userCountResponse.status === 'fulfilled') setUserCount(Number(userCountResponse.value?.count) || 0);
      } catch (e) { console.warn('[Subscription] usage fetch failed:', e?.message || e); }

      try {
        const { data: subData, error: subError } = await supabase
          .from('subscriptions').select('*').eq('organization_id', orgId)
          .order('created_at', { ascending: false }).limit(1).maybeSingle();
        setLatestSubscription(subError ? null : (subData || null));
      } catch (_) { setLatestSubscription(null); }
    } catch (e) {
      console.error('[Subscription] fetchOrg error:', e);
    } finally {
      setLoadingTrial(false);
    }
  }, [orgId, authOrganization]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  // Realtime solo si el cobro está activo para esta org.
  useEffect(() => {
    if (!orgId || !billingEnabled) return undefined;
    const channel = supabase
      .channel(`org-billing-${orgId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${orgId}` }, () => fetchOrg({ force: true }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscriptions', filter: `organization_id=eq.${orgId}` }, () => fetchOrg({ force: true }))
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch { /* noop */ } };
  }, [orgId, billingEnabled, fetchOrg]);

  useEffect(() => {
    const handler = () => { fetchOrg({ force: true }); };
    window.addEventListener(USAGE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(USAGE_CHANGED_EVENT, handler);
  }, [fetchOrg]);

  const derived = useMemo(() => {
    // FLAG OFF → estado inerte, sin importar la data de la org.
    if (!billingEnabled) return { ...INERT_VALUE };

    const now = new Date();
    const trialEndsAt = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;
    const trialStartsAt = org?.trial_starts_at ? new Date(org.trial_starts_at) : null;
    const msLeft = trialEndsAt ? trialEndsAt - now : 0;
    const daysLeft = trialEndsAt ? Math.max(0, Math.ceil(msLeft / 86400000)) : 0;

    const subStatus = org?.subscription_status || org?.status || 'trial';

    let graceDaysLeft = 0;
    if (trialStartsAt && (subStatus === 'grace' || subStatus === 'trial')) {
      const day30 = new Date(trialStartsAt.getTime() + 30 * 86400000);
      graceDaysLeft = Math.max(0, Math.ceil((day30 - now) / 86400000));
    }
    if (org?.subscription_expires_at && subStatus === 'grace') {
      const graceEnd = new Date(new Date(org.subscription_expires_at).getTime() + 16 * 86400000);
      graceDaysLeft = Math.max(0, Math.ceil((graceEnd - now) / 86400000));
    }

    const isTrialing = subStatus === 'trial' && daysLeft > 0;
    const isActive = subStatus === 'active';
    const isPending = subStatus === 'payment_pending' || org?.status === 'payment_pending';
    const isGrace = subStatus === 'grace';
    const isExpired = subStatus === 'expired';
    const isBlocked = subStatus === 'blocked' || org?.status === 'blocked';
    const isReadOnly = org?.read_only_mode === true || isExpired;
    const showGraceWarning = isGrace && graceDaysLeft <= 5 && graceDaysLeft > 0;
    const showWarning = isTrialing && daysLeft <= 5;
    const paymentUrl = latestSubscription?.status === 'pending' ? latestSubscription.payment_url : null;
    const currentPlanKey = org?.current_plan_key || org?.plan || 'basic';
    const subscriptionExpiresAt = org?.subscription_expires_at ? new Date(org.subscription_expires_at) : null;

    const billingProvider = BILLING_BRAND.paymentProvider;
    const billingProviderMeta = getSubscriptionPaymentProviderMeta(billingProvider);

    const limits = currentPlan?.monthly_limits || { ...ZERO_LIMITS };
    const maxUsers = Number(currentPlan?.max_users) || 1;
    const usageMap = USAGE_KINDS.reduce((acc, kind) => {
      const used = Number(usage?.[kind]) || 0;
      const limit = Number(limits?.[kind]) || 0;
      acc[kind] = {
        used, limit, unlimited: limit === 0,
        reached: limit > 0 && used >= limit,
        percentage: limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0,
        remaining: limit > 0 ? Math.max(0, limit - used) : null,
      };
      return acc;
    }, {});
    const usersLimit = {
      used: userCount, limit: maxUsers, unlimited: maxUsers === 0,
      reached: maxUsers > 0 && userCount >= maxUsers,
      percentage: maxUsers > 0 ? Math.min(100, Math.round((userCount / maxUsers) * 100)) : 0,
      remaining: maxUsers > 0 ? Math.max(0, maxUsers - userCount) : null,
    };
    const canDo = (kind) => {
      if (kind === 'users') return !usersLimit.reached;
      const meta = usageMap[kind];
      return meta ? !meta.reached : true;
    };

    return {
      daysLeft, isTrialing, isActive, isPending, isGrace, isExpired, isBlocked, isReadOnly,
      showWarning, showGraceWarning, graceDaysLeft, paymentUrl, currentPlanKey, subscriptionExpiresAt,
      status: subStatus, billingEnabled: true,
      billingProvider, billingProviderLabel: billingProviderMeta.label, billingProviderMeta,
      limits, maxUsers, userCount, usage, usageMap, usersLimit, canDo,
    };
  }, [billingEnabled, currentPlan, org, latestSubscription, usage, userCount]);

  return (
    <SubscriptionContext.Provider value={{
      ...derived,
      organization: org,
      currentPlan,
      latestSubscription,
      loadingTrial,
      refreshTrial: fetchOrg,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionStatus = () => useContext(SubscriptionContext);

// ── Compat con el código portado de Gastify (mismos nombres) ──────────
export const TrialProvider = SubscriptionProvider;
export const useTrial = useSubscriptionStatus;

export default useSubscriptionStatus;
